/**
 * Утилиты для sanitization пользовательского контента (UGC)
 * Защита от XSS атак с помощью DOMPurify
 */

import DOMPurify, { type Config } from 'dompurify';

/**
 * Конфигурация DOMPurify для разных типов контента
 */
const SANITIZE_CONFIG = {
  // Строгая очистка - только текст, никакого HTML
  strict: {
    ALLOWED_TAGS: [] as string[],
    ALLOWED_ATTR: [] as string[],
    KEEP_CONTENT: true,
  },
  // Базовая очистка - разрешены простые теги форматирования
  basic: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br'],
    ALLOWED_ATTR: [] as string[],
  },
} as const;

/**
 * Очищает пользовательский текст от вредоносного кода
 * 
 * @param input - Текст от пользователя (может содержать XSS)
 * @param mode - Режим очистки ('strict' | 'basic')
 * @returns Безопасный текст
 * 
 * @example
 * const userInput = '<script>alert("hack")</script>Моя мечта';
 * sanitizeUserInput(userInput); // "Моя мечта"
 */
export function sanitizeUserInput(
  input: string | null | undefined,
  mode: 'strict' | 'basic' = 'strict'
): string {
  // Пустые значения возвращаем как пустую строку
  if (!input) return '';

  // Применяем DOMPurify с нужной конфигурацией
  const cleaned = DOMPurify.sanitize(input, SANITIZE_CONFIG[mode] as Config) as unknown as string;

  // Дополнительная защита: trim() убирает пробелы по краям
  return cleaned.trim();
}

/**
 * Очищает массив строк (например, теги, категории)
 */
export function sanitizeStringArray(
  items: string[] | null | undefined
): string[] {
  if (!items || !Array.isArray(items)) return [];

  return items
    .map(item => sanitizeUserInput(item, 'strict'))
    .filter(item => item.length > 0); // Убираем пустые после очистки
}

/**
 * Типобезопасная обёртка для sanitization объектов с UGC
 * Используется для очистки данных из API перед отображением
 * 
 * @example
 * const wishlist = { title: '<script>hack</script>Dream', description: 'Safe text' };
 * const safe = sanitizeObject(wishlist, ['title', 'description']);
 * // { title: 'Dream', description: 'Safe text' }
 */
export function sanitizeObject<T extends Record<string, unknown>>(
  obj: T,
  fields: (keyof T)[],
  mode: 'strict' | 'basic' = 'strict'
): T {
  const sanitized = { ...obj };

  for (const field of fields) {
    const value = sanitized[field];
    if (typeof value === 'string') {
      sanitized[field] = sanitizeUserInput(value, mode) as T[keyof T];
    }
  }

  return sanitized;
}